import React, { useContext, useEffect, useState } from 'react'
import Typography from '@material-ui/core/Typography'
import { Controller, useFormContext } from 'react-hook-form'
import { Box, Grid, Select, MenuItem, FormControl, InputLabel, TextField } from '@material-ui/core'
import moment from 'moment'
import { map, size } from 'lodash'
import { useTranslation } from 'react-i18next'
import { Context } from '../Store'
import { ErrorMessage } from '@hookform/error-message'
import ErrorMessageComponent from '../utils/ErrorMessage'
import CheckInDatePicker from '../CheckInDatePicker/CheckInDatePicker'
import CheckOutDatePicker from '../CheckOutDatePicker/CheckOutDatePicker'
import { Alert } from '@material-ui/lab'

const checkIfAreBookingInTimeInterval = (checkIn, checkOut, bookings) => {
  let results = map(
    bookings,
    (booking) => moment(booking[0]).isBetween(checkIn, checkOut) && moment(booking[1]).isBetween(checkIn, checkOut),
  )
  if (size(results) === 0) {
    return false
  }
  return results.reduce((result, condition) => result || condition)
}

const CheckInStep = (props) => {
  const { control, errors, watch, setError, clearErrors, setValue } = useFormContext()
  const values = watch()
  const { t } = useTranslation()
  const [state] = useContext(Context)
  const { property } = state
  const { minStay = 1, maxStay, end_date: propertyEndDate, notAvailablePeriods } = property
  const remappedNotAvailablePeriods = map(notAvailablePeriods, (period) => ({
    endDate: moment(period[1]),
    startDateWithMinStay: moment(period[0]).subtract(minStay, 'months'),
    startDate: moment(period[0]),
  }))
  const durationValues = [
    {
      value: 'under',
      label: t('booking.checkIn.duration.under12Months'),
    },
    {
      value: 'over',
      label: t('booking.checkIn.duration.over12Months'),
      disabled: maxStay && maxStay <= 12,
    },
  ]
  const [selectDisabled, setSelectDisabled] = useState(false)

  const [showCheckInWarningMessage, setShowCheckInWarningMessage] = useState(false)
  useEffect(() => {
    if (
      moment(values.start_date).isSameOrAfter(values.end_date) ||
      moment(values.start_date).add(minStay, 'months').isSameOrAfter(values.end_date)
    ) {
      setValue('end_date', null)
    }
    if (
      moment(propertyEndDate).subtract(minStay, 'months').isBefore(moment(values.start_date).add(12, 'months')) &&
      propertyEndDate
    ) {
      setSelectDisabled(true)
      setValue('over12Months', 'under')
    } else if (selectDisabled) {
      setSelectDisabled(false)
      if (moment(values.start_date).isValid() && (values.over12Months || values.no_guest)) {
        setValue('over12Months', moment(values.end_date).diff(values.start_date, 'months', true) < 12 ? 'under' : 'over')
      }
    }
    if (values && values.start_date && moment(values.start_date).isSameOrBefore(moment().add(20, 'days'))) {
      setShowCheckInWarningMessage(true)
    } else {
      setShowCheckInWarningMessage(false)
    }
  }, [values.start_date])
  useEffect(() => {
    clearErrors(['start_date'])
    if (values.over12Months === 'over' || errors.start_date) {
      const endDate = moment(values.start_date).add(12, 'months')
      const containsBookings = checkIfAreBookingInTimeInterval(values.start_date, endDate, notAvailablePeriods)
      if (containsBookings) {
        setError('start_date', {
          type: 'manual',
          message: t('booking.checkIn.dateCheckIn.errors.containsBooking'),
        })
      }
    }
  }, [values.start_date, values.over12Months])

  return (
    <>
      <Grid container direction="row" justify="space-between" alignItems="stretch" spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h4">{t('booking.title')}</Typography>
        </Grid>
        <Grid item xs={12} lg={10}>
          <Controller
            name="start_date"
            defaultValue={null}
            render={(props) => (
              <CheckInDatePicker
                value={props.value}
                onChange={props.onChange}
                onBlur={props.onBlur}
                notAvailablePeriods={remappedNotAvailablePeriods}
                minStay={minStay}
              />
            )}
          />
          <ErrorMessage errors={errors} name="start_date" render={ErrorMessageComponent} />
        </Grid>
        {showCheckInWarningMessage && (
          <Grid item xs={12} lg={10}>
            <Alert severity="warning">{t('booking.checkIn.warningMessage')}</Alert>
          </Grid>
        )}
        <Grid item xs={12} lg={10}>
          <FormControl fullWidth variant="outlined">
            <InputLabel>{t('booking.checkIn.duration.placeholder')}</InputLabel>
            <Controller
              name="over12Months"
              control={control}
              defaultValue=""
              error={errors.over12Months}
              as={
                <Select disabled={selectDisabled} label={t('booking.checkIn.duration.placeholder')}>
                  {map(durationValues, (value) => (
                    <MenuItem disabled={value.disabled} key={value.value} value={value.value}>
                      {value.label}
                    </MenuItem>
                  ))}
                </Select>
              }
            />
          </FormControl>
        </Grid>
        <Grid
          item
          xs={12}
          lg={10}
          style={{
            display: values.over12Months === 'under' ? 'flex' : 'none',
          }}
        >
          <Controller
            name="end_date"
            defaultValue={null}
            render={(props) => (
              <CheckOutDatePicker
                maxStay={maxStay}
                value={props.value}
                onChange={props.onChange}
                onBlur={props.onBlur}
                notAvailablePeriods={remappedNotAvailablePeriods}
                minStay={minStay}
              />
            )}
          />
        </Grid>
        <Grid
          item
          xs={12}
          lg={10}
          style={{
            display: values.over12Months ? 'flex' : 'none',
            flexDirection: 'column',
          }}
        >
          <Controller
            name="no_guest"
            as={
              <TextField
                fullWidth
                error={errors.no_guest}
                variant="outlined"
                label={t('booking.checkIn.people.placeholder')}
                inputProps={{
                  maxLength: 1,
                }}
              />
            }
          />
          <ErrorMessage errors={errors} name="no_guest" render={ErrorMessageComponent} />
        </Grid>
      </Grid>
    </>
  )
}

export default CheckInStep
